import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import CompanySummaryCards from './components/CompanySummaryCards';
import CompaniesTable from './components/CompaniesTable';
import DeleteCompanyDialog from './components/DeleteCompanyDialog';
import MarkCompanyTestDialog from './components/MarkCompanyTestDialog';
import useAdminCompanies from './hooks/useAdminCompanies';
import { SetAdminCompanyTestAPI } from '../../../api/AdminAPI';

/**
 * AdminCompanies - Listagem de empresas do Admin Geral (área restrita à plataforma)
 *
 * Responsabilidades:
 * - Consome useAdminCompanies() para estado de filtros, ordenação, paginação e dados
 * - Renderiza AreaTitle + cards de resumo + tabela com filtros e paginação server-side
 * - Orquestra ações de linha: ver detalhes, marcar/desmarcar teste, excluir empresa
 */
function AdminCompanies() {
  const navigate = useNavigate();
  const {
    companies,
    summary,
    pagination,
    isLoading,
    error,
    searchInput,
    setSearchInput,
    status,
    setStatus,
    isTest,
    setIsTest,
    closure,
    setClosure,
    sortValue,
    setSortValue,
    setPage,
    setLimit,
    refresh,
  } = useAdminCompanies();

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [companyToMarkTest, setCompanyToMarkTest] = useState(null);
  const [markingTest, setMarkingTest] = useState(false);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleView = (id) => {
    navigate(`/admin/companies/${id}`);
  };

  const applyTest = (company, nextIsTest, onDone) => {
    SetAdminCompanyTestAPI(company.id, nextIsTest, (response) => {
      if (onDone) onDone();
      if (!response || response.success === false) {
        showToast(response?.error || 'Erro ao atualizar a empresa. Tente novamente.', 'error');
        return;
      }
      showToast(nextIsTest ? 'Empresa marcada como teste.' : 'Empresa desmarcada como teste.', 'success');
      refresh();
    });
  };

  // Marcar como teste libera checkout gratuito no Stripe: sempre confirma antes.
  const handleToggleTest = (company) => {
    const nextIsTest = !(Number(company.is_test) === 1);
    if (nextIsTest) {
      setCompanyToMarkTest(company);
      return;
    }
    applyTest(company, false);
  };

  const handleMarkTestConfirm = () => {
    if (!companyToMarkTest || markingTest) return;
    setMarkingTest(true);
    applyTest(companyToMarkTest, true, () => {
      setMarkingTest(false);
      setCompanyToMarkTest(null);
    });
  };

  const handleDeleteRequest = (company) => {
    setCompanyToDelete(company);
  };

  const handleDeleteCancel = () => {
    setCompanyToDelete(null);
  };

  const handleDeleted = () => {
    setCompanyToDelete(null);
    showToast('Empresa excluída permanentemente.', 'success');
    refresh();
  };

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Empresas"
          Icon={<AdminPanelSettingsOutlinedIcon />}
          Description="Acompanhe tração, uso e saúde de todas as empresas cadastradas na plataforma."
        />
      </Box>

      <CompanySummaryCards summary={summary} />

      <CompaniesTable
        companies={companies}
        isLoading={isLoading}
        error={error}
        pagination={pagination}
        filters={{
          searchInput,
          onSearchChange: setSearchInput,
          status,
          onStatusChange: setStatus,
          isTest,
          onIsTestChange: setIsTest,
          closure,
          onClosureChange: setClosure,
          sortValue,
          onSortChange: setSortValue,
        }}
        onPageChange={setPage}
        onLimitChange={setLimit}
        onView={handleView}
        onToggleTest={handleToggleTest}
        onDelete={handleDeleteRequest}
      />

      <MarkCompanyTestDialog
        isOpen={Boolean(companyToMarkTest)}
        company={companyToMarkTest}
        loading={markingTest}
        onConfirm={handleMarkTestConfirm}
        onCancel={() => setCompanyToMarkTest(null)}
      />

      <DeleteCompanyDialog
        isOpen={Boolean(companyToDelete)}
        company={companyToDelete}
        onCancel={handleDeleteCancel}
        onDeleted={handleDeleted}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminCompanies;
